import React from 'react';
import Modal from '../common/Modal';
import FormInput from '../forms/FormInput';
import SelectInput from '../forms/SelectInput';
import Button from '../common/Button';
import { useForm } from '../../hooks/useForm';
import { validateAddress } from '../../utils/addressValidator';
import { theme } from '../../config/theme';

const ADDRESS_LABELS = [
  { value: 'home', label: 'Home' },
  { value: 'work', label: 'Work' },
  { value: 'other', label: 'Other' },
];

const AddressForm = ({ isOpen, onClose, address, onSubmit, loading }) => {
  const isEdit = !!address;

  const EMPTY_ADDRESS = {
    label: 'home',
    full_name: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'India',
    is_default: false,
  };

  const { values, errors, touched, handleChange, handleBlur, setValues, setErrors, setTouched, resetForm } = useForm(
    address ? {
      label: address.label || 'home',
      full_name: address.full_name || '',
      phone: address.phone || '',
      address_line1: address.address_line1 || '',
      address_line2: address.address_line2 || '',
      city: address.city || '',
      state: address.state || '',
      postal_code: address.postal_code || '',
      country: address.country || 'India',
      is_default: address.is_default || false,
    } : EMPTY_ADDRESS
  );

  React.useEffect(() => {
    if (address) {
      // Populate form when editing an existing address
      setValues({
        label: address.label || 'home',
        full_name: address.full_name || '',
        phone: address.phone || '',
        address_line1: address.address_line1 || '',
        address_line2: address.address_line2 || '',
        city: address.city || '',
        state: address.state || '',
        postal_code: address.postal_code || '',
        country: address.country || 'India',
        is_default: address.is_default || false,
      });
    } else {
      // Reset form when switching to "Add New" mode
      resetForm();
    }
  }, [address]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validation = validateAddress(values);
    if (!validation.isValid) {
      // Set errors AND mark all errored fields as touched so FormInput renders them
      setErrors(validation.errors);
      const touchedFields = Object.keys(validation.errors).reduce(
        (acc, key) => ({ ...acc, [key]: true }),
        {}
      );
      setTouched(prev => ({ ...prev, ...touchedFields }));
      return;
    }

    const result = await onSubmit(values);
    if (result?.success) {
      resetForm();
      onClose();
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEdit ? 'Edit Address' : 'Add New Address'}
      maxWidth="600px"
    >
      <form onSubmit={handleSubmit}>
        <SelectInput
          label="Address Type"
          name="label"
          value={values.label}
          onChange={handleChange}
          options={ADDRESS_LABELS}
          required
        />

        <FormInput
          label="Full Name"
          name="full_name"
          value={values.full_name}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.full_name}
          touched={touched.full_name}
          required
        />

        <FormInput
          label="Phone Number"
          name="phone"
          type="tel"
          value={values.phone}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.phone}
          touched={touched.phone}
          placeholder="9876543210"
          required
        />

        <FormInput
          label="Address Line 1"
          name="address_line1"
          value={values.address_line1}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.address_line1}
          touched={touched.address_line1}
          placeholder="House/Flat No., Building Name"
          required
        />

        <FormInput
          label="Address Line 2"
          name="address_line2"
          value={values.address_line2}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="Street, Area, Landmark"
        />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing.md }}>
          <FormInput
            label="City"
            name="city"
            value={values.city}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.city}
            touched={touched.city}
            required
          />
          <FormInput
            label="State"
            name="state"
            value={values.state}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.state}
            touched={touched.state}
            required
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing.md }}>
          <FormInput
            label="Postal Code"
            name="postal_code"
            value={values.postal_code}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.postal_code}
            touched={touched.postal_code}
            placeholder="400001"
            required
          />
          <FormInput
            label="Country"
            name="country"
            value={values.country}
            onChange={handleChange}
            disabled
          />
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm, cursor: 'pointer', marginTop: theme.spacing.md }}>
          <input
            type="checkbox"
            name="is_default"
            checked={values.is_default}
            onChange={(e) => handleChange({ target: { name: 'is_default', value: e.target.checked } })}
          />
          <span>Set as default address</span>
        </label>

        <div style={{ display: 'flex', gap: theme.spacing.md, marginTop: theme.spacing.xl }}>
          <Button type="button" variant="secondary" onClick={handleClose} style={{ flex: 1 }}>
            Cancel
          </Button>
          <Button type="submit" loading={loading} disabled={loading} style={{ flex: 1 }}>
            {isEdit ? 'Update Address' : 'Add Address'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AddressForm;